<template>
    <Container
        v-if="hasCachedPosts || hasLoadedPosts"
        class="tw-pb-10"
        :hide-container="hideContainer">
        <template v-if="hasPosts">
            <OpinionsList :posts="allPosts" @fetch-more="fetchMore" />
        </template>

    </Container>
    <Loader v-else />
</template>

<script setup lang="ts">
    import Loader from './loader.vue';
    import { fetchOpinions } from '@/api/posts';
    import { getOpinionsFromCache } from '@/composables/usePost';
    import { POSTS_PER_PAGE } from '@/configs/posts';
    import type { IOpinion } from '@/contentTypes';
    import { useAppStore } from '@/store/app';
    import _ from 'lodash';

    const props = defineProps<{
        hideContainer?: boolean;
    }>();

    const hasLoadedPosts = inject('hasLoadedPosts', ref(false));
    const appStore = useAppStore();
    const $route = useRoute();

    const hasCachedPosts = ref(false);
    const isLoadingPage = ref(false);
    const forceLoading = ref(false);

    const cachedPosts = ref<IOpinion[]>([]);
    const posts = ref<IOpinion[]>([]);
    const page = ref(1);

    const allPosts = computed(() => {
        return [...cachedPosts.value, ...posts.value];
    });

    const totalPosts = computed(() => {
        return appStore.getPostsTotalByCategory('opinions') || 0;
    });

    const hasPosts = computed(() => {
        return allPosts.value.length > 0;
    });

    async function fetchMore() {
        page.value++;
        isLoadingPage.value = true;

        const query = {
            page: page.value, // Increment page number
            per_page: POSTS_PER_PAGE,
        };
        try {
            const { data } = await fetchOpinions(query);
            if (data && data.posts) {
                posts.value.push(...data.posts);
            }
        } catch (error) {
            console.error('Error fetching more posts:', error);
        } finally {
            isLoadingPage.value = false;
        }
    }

    async function init() {
        forceLoading.value = true;
        const postsData = await getOpinionsFromCache();
        if (!postsData || postsData.length === 0) {
            posts.value = [];
            return;
        }

        hasCachedPosts.value = true;

        // all posts
        cachedPosts.value = _.cloneDeep(postsData);

        if (posts.value.length > 0) {
            // check if the cached and first post are in the posts, if they are, remove them from the posts
            const cachedPostIds = new Set(cachedPosts.value.map((post) => post.id));
            posts.value = posts.value.filter((post) => {
                return !cachedPostIds.has(post.id);
            });
        }
        forceLoading.value = false;
    }

    init();

    watch(hasLoadedPosts, async (newValue) => {
        if (newValue) {
            init();
        }
    });
</script>
<style scoped></style>

